import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  RemoveCircleOutline
} from '@mui/icons-material';
import axios from 'axios';
import useAxios from 'axios-hooks';

import { useAuth } from 'hooks/use-auth';
import {
  Equipment,
  ProductionState,
  productionStatesToStrings,
  Production,
  parseProductions
} from 'models';
import Dialog, { ButtonType } from 'components/Dialog';
import {
  StateIndicator
} from 'components/Production';


interface Props {
  equipment?: Equipment
}

export default function EquipmentProductionPage({equipment}: Props) {
  const auth = useAuth();
  return (
    <>
    {equipment && (
      <ProductionList
        companyId={auth.companyId}
        factoryId={auth.factoryId}
        equipmentId={equipment.id}
      />
    )}
    </>
  );
}

// --------------------------------------------------------------------------------------
// ProductionList component

interface ProductionListProps {
  companyId: number;
  factoryId: number;
  equipmentId: string;
  previewStates?: ProductionState[];
  update?: any;
  onChange?: () => void;
}

function ProductionList({
  companyId,
  factoryId,
  equipmentId,
  previewStates = [ProductionState.Running, ProductionState.Done],
  update,
  onChange = () => {}
}: ProductionListProps) {
  const auth = useAuth();

  const [productions, setProductions] = useState<Production[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Production>();

  const [{data, loading, error}, refetch] = useAxios({
    url: `/v0/equipments/${equipmentId}/productions`,
    headers: auth.authorizationHeader,
    params: {
      factory_id: factoryId,
      states: productionStatesToStrings(previewStates).join(',')
    }
  });

  function handleClickRemove(row: any) {
    setRemoveTarget(row);
    setOpenDialog(true);
  }

  function handleClickRemoveSubmit() {
    if (removeTarget) {
      axios.delete(`/v0/equipments/${equipmentId}/productions`, {
        headers: auth.authorizationHeader,
        data: {
          factory_id: factoryId,
          product_id: removeTarget?.productId,
          production_id: removeTarget?.id,
        }
      }).then((res) => {
        setProductions(productions.filter(a => (
          a.id != removeTarget.id
        )));
        alert('削除しました');
      }).catch((err) => {
        console.error(err);
        alert('サーバーとの通信に失敗しました');
      }).finally(() => {
        setOpenDialog(false);
        setRemoveTarget(undefined);
      });
    }
  }

  useEffect(() => {
    refetch();
  }, [update])

  useEffect(() => {
    setProductions(parseProductions(data)?.sort((a, b) => (
      (b.state === ProductionState.Running ? 1 : -1) ||
      (b.runningTimeTo?.getTime() || 0) - (a.runningTimeTo?.getTime() || 0)
    )));
  }, [data]);


  if (loading) {
    return (<p>Loading...</p>);
  }

  return (
    <Box>
    {error ? (
      <Alert severity="error">
        製作履歴の読み込みに失敗しました
      </Alert>
    ) : (
      <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">状態</TableCell>
              <TableCell align="left">製品ID</TableCell>
              <TableCell align="left">製品名</TableCell>
              <TableCell align="right">数量</TableCell>
              <TableCell>開始日時</TableCell>
              <TableCell>終了日時</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {productions?.map((p) => (
            <TableRow
              key={p.id}
              sx={{'&:last-child td, &:last-child th': {border: 0}}}
            >
              <TableCell align="center">
                <StateIndicator state={p.state} />
              </TableCell>
              <TableCell align="left">{p.productId}</TableCell>
              <TableCell align="left">{p.productName} {p.productModel}</TableCell>
              <TableCell align="right">{p.quantity}</TableCell>
              <TableCell>{p.runningTimeFrom?.toLocaleString()}</TableCell>
              <TableCell>{p.runningTimeTo?.toLocaleString()}</TableCell>
              <TableCell>
                <IconButton
                  color="error"
                  onClick={() => handleClickRemove(p)}
                >
                  <RemoveCircleOutline/>
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={openDialog && removeTarget}
        buttonType={ButtonType.YesNo}
        onClose={() => setOpenDialog(false)}
        onClickYes={handleClickRemoveSubmit}
        onClickNo={() => setOpenDialog(false)}
      >
        次の製作履歴を削除してもよろしいですか？<br/>
        [{removeTarget?.productId}] {removeTarget?.productName}<br/>
        開始: {removeTarget?.runningTimeFrom?.toLocaleString()}<br/>
        終了: {removeTarget?.runningTimeTo?.toLocaleString()}
      </Dialog>
      </>
    )}
    </Box>
  );
}

